import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { Link as RouterLink } from 'react-router-dom'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import { useCrashReport } from '../hooks/useCrashReports'
import { CrashKindChip } from './CrashKindChip'
import { StackBlock } from './StackBlock'
import { formatDateTime, formatPlatform, formatVersion } from './crashFormat'

interface CrashReportDialogProps {
  id: string | null
  onClose: () => void
}

/** One crash report: every metadata field plus the JS/native stack and the React component stack. */
export function CrashReportDialog({ id, onClose }: CrashReportDialogProps) {
  const dialogProps = useMobileDialogProps()
  const detail = useCrashReport(id)
  const report = detail.data
  // Source-mapped by default when the build's map was uploaded; the minified original stays one tap away.
  const [view, setView] = useState<'source' | 'minified'>('source')
  const mapped = !!(report?.symbolicatedStack || report?.symbolicatedComponentStack)
  const showSource = mapped && view === 'source'

  return (
    <Dialog open={id != null} onClose={onClose} maxWidth="md" fullWidth {...dialogProps}>
      <DialogTitle sx={{ pr: 6 }}>
        Crash report
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {detail.isLoading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : detail.isError || !report ? (
          <Alert severity="warning">That report could not be loaded. It may have been cleared.</Alert>
        ) : (
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <CrashKindChip kind={report.kind} />
              <Chip
                size="small"
                label={report.isFatal ? 'Fatal' : 'Non-fatal'}
                color={report.isFatal ? 'error' : 'default'}
              />
            </Stack>

            <Typography variant="body2" sx={{ wordBreak: 'break-word', fontWeight: 500 }}>
              {report.message || '(no message)'}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Detail label="Occurred" value={formatDateTime(report.occurredAt)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Detail label="Received" value={formatDateTime(report.receivedAt)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={0.25}>
                  <DetailLabel>User</DetailLabel>
                  {report.userId ? (
                    <Link component={RouterLink} to={`/users/${report.userId}`} variant="body2">
                      {report.userDisplayName || report.userId}
                    </Link>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Signed out
                    </Typography>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Detail label="Platform" value={formatPlatform(report.platform)} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Detail label="OS" value={report.osVersion || '—'} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Detail label="Device" value={report.deviceModel || '—'} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Detail label="App version" value={formatVersion(report.appVersion, report.buildNumber)} />
              </Grid>
              <Grid item xs={12}>
                <Detail label="Screen / route" value={report.route || '—'} mono />
              </Grid>
              <Grid item xs={12}>
                <Detail label="Fingerprint" value={report.fingerprint} mono />
              </Grid>
              <Grid item xs={12}>
                <Detail label="Report id" value={report.id} mono />
              </Grid>
            </Grid>

            <Divider />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {mapped ? (
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={view}
                  onChange={(_, next: 'source' | 'minified' | null) => next && setView(next)}
                >
                  <ToggleButton value="source">Source</ToggleButton>
                  <ToggleButton value="minified">Minified</ToggleButton>
                </ToggleButtonGroup>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No source map for this build, so the stack is minified.
                </Typography>
              )}
            </Stack>
            <StackBlock title="Stack" text={showSource ? report.symbolicatedStack ?? report.stack : report.stack} />
            <StackBlock
              title="Component stack"
              text={showSource ? report.symbolicatedComponentStack ?? report.componentStack : report.componentStack}
            />
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  )
}

function DetailLabel({ children }: { children: string }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
      {children}
    </Typography>
  )
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Stack spacing={0.25}>
      <DetailLabel>{label}</DetailLabel>
      <Typography
        variant="body2"
        sx={{
          fontFamily: mono ? 'ui-monospace, monospace' : undefined,
          fontSize: mono ? 12 : undefined,
          wordBreak: 'break-all',
        }}
      >
        {value}
      </Typography>
    </Stack>
  )
}
